import { useCallback, useMemo, type RefObject } from "react";
import type { Editor } from "@tiptap/core";
import type { Variables } from "@/lib/sparxs/metadataSchemas";
import { getAvailableDirectives } from "../../directives/core/config";
import type { DirectiveChipStrategy } from "../../directives/core/chipStrategy";
import type { DirectiveChipEditSegment } from "../../types/directiveChipTypes";
import {
  runChoiceSubmit,
  runReorderPrintTableChoices,
  runSelectDirective,
  runSelectVariable,
  runUpdateChoiceOnly,
  runUpdateVariableOnly,
} from "./directiveChipNodeViewHandlers";
import type { DirectiveChipDerived } from "./directiveChipDerived";

export type UseDirectiveChipNodeViewActionsArgs = {
  editor: Editor;
  getPos: () => number | undefined;
  parsed: DirectiveChipDerived["parsed"];
  config: DirectiveChipDerived["config"];
  allVariables: Variables[];
  strategy: DirectiveChipStrategy | null;
  justCompletedRef: RefObject<boolean>;
  preventCloseOnceRef: RefObject<boolean>;
  focusChoiceSelectOnceRef: RefObject<boolean>;
  focusChoiceInputOnceRef: RefObject<boolean>;
  setOpen: (open: boolean) => void;
  setEditSegment: (segment: DirectiveChipEditSegment | null) => void;
};

export const useDirectiveChipNodeViewActions = ({
  editor,
  getPos,
  parsed,
  config,
  allVariables,
  strategy,
  justCompletedRef,
  preventCloseOnceRef,
  focusChoiceSelectOnceRef,
  focusChoiceInputOnceRef,
  setOpen,
  setEditSegment,
}: UseDirectiveChipNodeViewActionsArgs) => {
  const handleSelectVariable = useCallback(
    (variableName: string) => {
      runSelectVariable(variableName, {
        editor,
        getPos,
        parsed,
        config,
        allVariables,
        strategy,
        justCompletedRef,
        preventCloseOnceRef,
        focusChoiceSelectOnceRef,
        focusChoiceInputOnceRef,
        setOpen,
      });
    },
    [
      editor,
      getPos,
      parsed,
      config,
      allVariables,
      strategy,
      justCompletedRef,
      preventCloseOnceRef,
      focusChoiceSelectOnceRef,
      focusChoiceInputOnceRef,
      setOpen,
    ],
  );

  const handleChoiceSubmit = useCallback(
    (value: string) => {
      runChoiceSubmit(value, {
        editor,
        getPos,
        parsed,
        config,
        allVariables,
        strategy,
        justCompletedRef,
        preventCloseOnceRef,
        focusChoiceSelectOnceRef,
        focusChoiceInputOnceRef,
        setOpen,
      });
    },
    [
      editor,
      getPos,
      parsed,
      config,
      allVariables,
      strategy,
      justCompletedRef,
      preventCloseOnceRef,
      focusChoiceSelectOnceRef,
      focusChoiceInputOnceRef,
      setOpen,
    ],
  );

  const availableDirectives = useMemo(
    () => getAvailableDirectives(allVariables),
    [allVariables],
  );

  const handleSelectDirective = useCallback(
    (directiveKey: string) => {
      runSelectDirective(directiveKey, {
        editor,
        getPos,
        setEditSegment,
        setOpen,
      });
    },
    [editor, getPos, setEditSegment, setOpen],
  );

  const handleUpdateVariableOnly = useCallback(
    (variableName: string) => {
      runUpdateVariableOnly(variableName, {
        editor,
        getPos,
        parsed,
        setEditSegment,
        setOpen,
      });
    },
    [editor, getPos, parsed, setEditSegment, setOpen],
  );

  const handleUpdateChoiceOnly = useCallback(
    (choiceIndex: number, value: string) => {
      runUpdateChoiceOnly(choiceIndex, value, {
        editor,
        getPos,
        parsed,
        config,
        allVariables,
        strategy,
        setEditSegment,
        setOpen,
      });
    },
    [
      editor,
      getPos,
      parsed,
      config,
      allVariables,
      strategy,
      setEditSegment,
      setOpen,
    ],
  );

  const handleReorderPrintTableChoices = useCallback(
    (fromIndex: number, toIndex: number) => {
      runReorderPrintTableChoices(fromIndex, toIndex, {
        editor,
        getPos,
        parsed,
      });
    },
    [editor, getPos, parsed],
  );

  const handleSegmentClick = useCallback(
    (segment: DirectiveChipEditSegment) => {
      setEditSegment(segment);
      setOpen(true);
    },
    [setEditSegment, setOpen],
  );

  return {
    availableDirectives,
    handleSelectVariable,
    handleChoiceSubmit,
    handleSelectDirective,
    handleUpdateVariableOnly,
    handleUpdateChoiceOnly,
    handleReorderPrintTableChoices,
    handleSegmentClick,
  };
};
